import { Injectable } from '@angular/core';
import { CoordinateSourceFormat, ParsedCoordinatePair } from './models/coordinate-search.model';

@Injectable({
  providedIn: 'root',
})
export class CoordinateNormalizerService {
  formatDisplay(pair: ParsedCoordinatePair): string {
    switch (pair.sourceFormat) {
      case 'dms':
        return this.formatDmsDisplay(pair);
      case 'ddm':
        return this.formatDdmDisplay(pair);
      case 'cardinal':
        // Cardinal input is canonicalized to DD display.
        return this.formatDdDisplay(pair);
      case 'dd':
      default:
        return this.formatDdDisplay(pair);
    }
  }

  private formatDdDisplay(pair: ParsedCoordinatePair): string {
    return `${this.trimTrailingZeros(pair.lat)},${this.trimTrailingZeros(pair.lng)}`;
  }

  private formatDmsDisplay(pair: ParsedCoordinatePair): string {
    const parsed = this.parseSexagesimalInput(pair.rawInput, true);
    if (parsed) {
      const [lat, lng] = parsed;
      return `${lat} ${lng}`;
    }
    const lat = this.decimalToDms(pair.lat, true);
    const lng = this.decimalToDms(pair.lng, false);
    return `${lat} ${lng}`;
  }

  private formatDdmDisplay(pair: ParsedCoordinatePair): string {
    const parsed = this.parseSexagesimalInput(pair.rawInput, false);
    if (parsed) {
      const [lat, lng] = parsed;
      return `${lat} ${lng}`;
    }
    const lat = this.decimalToDdm(pair.lat, true);
    const lng = this.decimalToDdm(pair.lng, false);
    return `${lat} ${lng}`;
  }

  /**
   * Builds canonical DMS/DDM display from parsed raw input so minute/second
   * precision is preserved exactly as typed, while enforcing:
   * - space-separated pair
   * - no comma
   * - cardinal suffix form
   */
  private parseSexagesimalInput(
    rawInput: string,
    withSeconds: boolean
  ): [string, string] | null {
    const tokenPattern = withSeconds
      ? /^(\d{1,3})°\s*(\d{1,2})'\s*(\d{1,2}(?:\.\d+)?)"\s*([NSEW])$/i
      : /^(\d{1,3})°\s*(\d{1,2}\.\d+)'\s*([NSEW])$/i;

    const commaParts = rawInput
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);
    const parts =
      commaParts.length === 2
        ? commaParts
        : rawInput.split(/\s+/).filter(Boolean);
    if (parts.length !== 2) {
      return null;
    }

    if (withSeconds) {
      const lat = parts[0].match(tokenPattern);
      const lng = parts[1].match(tokenPattern);
      if (!lat || !lng) {
        return null;
      }
      return [
        `${lat[1]}°${lat[2]}'${lat[3]}"${lat[4].toUpperCase()}`,
        `${lng[1]}°${lng[2]}'${lng[3]}"${lng[4].toUpperCase()}`,
      ];
    }

    const lat = parts[0].match(tokenPattern);
    const lng = parts[1].match(tokenPattern);
    if (!lat || !lng) {
      return null;
    }
    return [
      `${lat[1]}°${lat[2]}'${lat[3].toUpperCase()}`,
      `${lng[1]}°${lng[2]}'${lng[3].toUpperCase()}`,
    ];
  }

  private decimalToDms(value: number, isLat: boolean): string {
    const hemi = isLat ? (value >= 0 ? 'N' : 'S') : value >= 0 ? 'E' : 'W';
    const abs = Math.abs(value);
    const deg = Math.floor(abs);
    const minFloat = (abs - deg) * 60;
    const min = Math.floor(minFloat);
    const sec = (minFloat - min) * 60;
    const secStr = this.trimTrailingZeros(sec);
    return `${deg}°${min}'${secStr}"${hemi}`;
  }

  private decimalToDdm(value: number, isLat: boolean): string {
    const hemi = isLat ? (value >= 0 ? 'N' : 'S') : value >= 0 ? 'E' : 'W';
    const abs = Math.abs(value);
    const deg = Math.floor(abs);
    const min = (abs - deg) * 60;
    return `${deg}°${this.trimTrailingZeros(min)}'${hemi}`;
  }

  private trimTrailingZeros(n: number): string {
    const s = String(n);
    if (!s.includes('.')) {
      return s;
    }
    return s.replace(/\.?0+$/, '');
  }
}
